// ---------- audio: cell-fill sound ----------
// One sound in this file, for one moment: a cell gets painted. No sound plays anywhere else —
// not on a mistake, not on a milestone, not at the end, not during the ending's replay of the
// player's own taps. The Karplus-Strong plucked-string synthesis this file used to carry (a
// guitar pluck, a chord for a finished symbol, a rising fanfare at the end) is gone entirely,
// not just its loudest voice: all three were built on the same string model, and all three are
// the "guitar sound" this replaces.

import { S } from './state';

let actx: AudioContext | null = null;
// index = symbol; codes 10-17 (motifsFin's quarter/three-quarter tiers) continue the same
// pentatonic scale two octaves higher. Codes 18-27 (pointilleElargi's twenty-seven pigment x
// taille combinations) have no such graduation to extend — nine independent colours, not nine
// levels of one tone — so they reuse the same nine base notes instead of climbing a third
// octave, which would only have made the sheet shriller without meaning anything. Codes 28-39
// (pointilleRiche's four extra pigments) are the same case again — four more independent
// colours, not four more levels — so they keep cycling the same nine base notes rather than
// starting a fourth octave.
const notes = [
  0, 174.6, 196.0, 220.0, 261.6, 293.7, 329.6, 392.0, 440.0, 523.3, 587.3, 659.3, 784.0, 880.0,
  1046.5, 1174.7, 1318.5, 1568.0, 174.6, 196.0, 220.0, 261.6, 293.7, 329.6, 392.0, 440.0, 523.3,
  587.3, 174.6, 196.0, 220.0, 261.6, 293.7, 329.6, 392.0, 440.0, 523.3, 174.6, 196.0, 220.0,
];

function audio(): AudioContext {
  if (!actx) actx = new AudioContext();
  // on mobile the context starts suspended: it must be resumed on the first gesture
  if (actx.state === 'suspended') void actx.resume();
  return actx;
}

/**
 * Plays a note based on the symbol. `semi` raises pitch by N semitones — that's the combo you
 * hear, and it's what makes chaining zones feel good, kept from the sound this replaces.
 *
 * A two-layer pop rather than a plucked string: a sine "body" swooping down into the note over
 * 40ms, plus a soft octave-up "sparkle" a beat behind it. Both are gone in well under 150ms —
 * short enough that a sweep filling forty cells a frame never turns into noise, and nothing
 * about the shape reads as an instrument being played, just a small cheerful blip.
 */
export function pop(level: number, semi = 0): void {
  if (!S.sound || !level) return;
  const ac = audio();
  // Au-delà de la table (peintureNuancee et ses 256 couleurs), les neuf notes de base en boucle,
  // comme les pigments de pointilleRiche : sans ce repli, `notes[level]` indéfini donnait une
  // fréquence NaN, et l'oscillateur lève une exception — à chaque case peinte.
  const base = level < notes.length ? notes[level] : notes[1 + ((level - 1) % 9)];
  const f = base * Math.pow(2, semi / 12) * (0.995 + Math.random() * 0.01);
  const t0 = ac.currentTime;

  const body = ac.createOscillator();
  body.type = 'sine';
  body.frequency.setValueAtTime(f * 1.8, t0);
  body.frequency.exponentialRampToValueAtTime(f, t0 + 0.04);
  const bodyGain = ac.createGain();
  bodyGain.gain.setValueAtTime(0.0001, t0);
  bodyGain.gain.exponentialRampToValueAtTime(0.5, t0 + 0.006);
  bodyGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.1);
  body.connect(bodyGain).connect(ac.destination);
  body.start(t0);
  body.stop(t0 + 0.12);

  const sparkle = ac.createOscillator();
  sparkle.type = 'sine';
  sparkle.frequency.setValueAtTime(f * 3, t0 + 0.012);
  const sparkleGain = ac.createGain();
  sparkleGain.gain.setValueAtTime(0.0001, t0 + 0.012);
  sparkleGain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.022);
  sparkleGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
  sparkle.connect(sparkleGain).connect(ac.destination);
  sparkle.start(t0 + 0.012);
  sparkle.stop(t0 + 0.1);
}
